import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { api } from "@/lib/http.ts";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useId, useMemo, useState } from "react";
import { useArtifactViewCtx } from "../ArtifactViewContext.tsx";
import { useRegisterSecretSubmit } from "./secretSubmit.ts";

/** The route a credential card posts to: a path on our own API, and nothing else. */
const COMPUTER_SECRET_PATH =
  /^(\/api)?\/a\/threads\/\d+\/messages\/\d+\/artifacts\/[A-Za-z0-9_-]+\/computerSecret$/;

/**
 * A field for an API key the agent's computer uses, held in a secret manager
 * rather than on the computer.
 *
 * The typed value posts to `submitPath` and is never sent back to the thread,
 * so this holds it in local state and nowhere else — no artifact write, no
 * patch route, no draft persistence. Once it is submitted the field is gone and
 * there is nothing to re-render it from, which is the intended end state rather
 * than a limitation: the value now lives at its destination and the only way
 * back to it is a fresh request.
 *
 * `hint`, `savedText` and `submitLabel` come from the server, which knows where
 * the value goes.
 *
 * Saving is offered from the artifact footer, beside Cancel, rather than from a
 * button here — every other block leaves the commit to the footer, and the two
 * halves of one decision should not sit in different chrome. What the footer
 * gets is a callback; the value never leaves this component.
 */
export const SecretInputBlock = ({
  question,
  hint,
  savedText,
  submitLabel,
  host,
  submitPath,
  state,
}: {
  question: string;
  hint: string;
  savedText: string;
  submitLabel: string;
  /** The one host the key is sent to, shown apart from the agent's question. */
  host: string;
  submitPath?: string;
  state: "waiting" | "saving" | "saved" | "cancelled";
}) => {
  const [value, setValue] = useState("");
  const { artifactId, isSharedThread } = useArtifactViewCtx();
  // Card reuse only dedupes the same variable and host, so a thread can hold two
  // of these at once. A fixed id would point both labels at whichever field
  // rendered first.
  const fieldId = useId();

  const trimmed = value.trim();

  const submit = useMutation({
    // Takes no argument, and reads the value from this render instead. Passed
    // to `mutate`, the credential would be kept as the mutation's `variables` —
    // in the query cache, readable from devtools — for as long as the card
    // stays mounted, well after `onSuccess` has cleared it from state.
    mutationFn: async () => {
      // Only ever to this card's own route on our API. The path comes from the
      // server, and this is the one place a wrong one would send a credential
      // somewhere else.
      if (!submitPath || !COMPUTER_SECRET_PATH.test(submitPath)) {
        throw new Error("This card has no route to save to.");
      }
      await api.postServerPath(submitPath, { value: trimmed });
    },
    onSuccess: () => {
      // Drop it from component state the moment the server has it. The card is
      // about to repaint into its saved form, and holding the value until then
      // keeps it in memory for no reason.
      setValue("");
    },
    onError: () => {
      errorToast("Couldn't save that. Try again.");
    },
  });

  const pending = submit.isPending || state === "saving";
  const interactive = state !== "saved" && state !== "cancelled" && !isSharedThread && !!submitPath;

  const doSubmit = useCallback(() => {
    if (!trimmed || pending) return;
    submit.mutate();
  }, [trimmed, pending, submit.mutate]);

  // Before the early returns below: hooks cannot be called conditionally, and a
  // card with nothing to save registers nothing rather than skipping the hook.
  useRegisterSecretSubmit(
    useMemo(
      () =>
        interactive
          ? {
              artifactId,
              label: submitLabel,
              pendingLabel: "Saving…",
              canSubmit: trimmed.length > 0,
              isPending: pending,
              submit: doSubmit,
            }
          : null,
      [interactive, artifactId, submitLabel, trimmed, pending, doSubmit],
    ),
  );

  if (state === "saved") {
    return <p className="text-xs text-muted-foreground">{savedText}</p>;
  }
  if (state === "cancelled") {
    return <p className="text-xs text-muted-foreground">No credential was provided.</p>;
  }

  // A shared thread's viewer is not the account owner, so they cannot write to
  // this computer and should not be shown a field implying they can.
  if (isSharedThread) {
    return (
      <div className="space-y-1">
        <p className="text-sm">{question}</p>
        <p className="text-xs text-muted-foreground">Only the account owner can enter this.</p>
      </div>
    );
  }

  // Rendered outside a thread — a notification email — so there is no route to
  // post to. Say where to go rather than offering a field that cannot submit.
  if (!submitPath) {
    return (
      <div className="space-y-1">
        <p className="text-sm">{question}</p>
        <p className="text-xs text-muted-foreground">Open the thread to enter this.</p>
      </div>
    );
  }

  return (
    // Still a form, with no button in it: Enter submits, which is how the
    // connector credential dialogs behave too. Save itself lives in the footer.
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        doSubmit();
      }}
    >
      {/* Above the question and in our words, not the agent's: the question can
          say anything, and this line is what tells the user where the key goes. */}
      <p className="text-sm font-medium">
        Only used with <span className="font-mono">{host}</span>
      </p>
      <Label className="block text-sm" htmlFor={fieldId}>
        {question}
      </Label>
      <Input
        id={fieldId}
        type="password"
        name="credential"
        // Kept out of session replay altogether. Inputs are masked there, but
        // that masking can be loosened from the analytics console; a blocked
        // element is recorded as an empty box whatever that setting says.
        className="rr-block"
        // A password manager offering to save this as a site login would be
        // wrong on both counts — it is not a login and this is not its site.
        autoComplete="off"
        spellCheck={false}
        placeholder="Paste it here"
        value={value}
        disabled={pending}
        onChange={(e) => setValue(e.target.value)}
      />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </form>
  );
};
